import * as React from 'react';
import { classNames, useUid, useUncontrolled } from '@/utils';

export interface RangeFieldProps {
  /** The HTML ID attribute to be applied to the range input element (optional). */
  id?: string;
  /** The minimum value of the range input field (optional). */
  min?: number;
  /** The maximum value of the range input field (optional). */
  max?: number;
  /** The step value of the range input field (optional). */
  step?: number;
  /** The name attribute to be applied to the range input element (optional). */
  name?: string;
  /** The value of the range input field (optional). */
  value?: number;
  /** Whether the range input field should be disabled (optional). */
  disabled?: boolean;
  /** The default value of the range input field (optional). */
  defaultValue?: number;
  /** CSS class names to be applied to the label element (optional). */
  labelClassName?: string;
  /** The label to be displayed next to the range input field (optional). */
  label?: React.ReactNode;
  /** The help text to be displayed below the range input field (optional). */
  helpText?: React.ReactNode;
  /**
   * A function that is called when the value of the range input field changes (optional).
   * This function accepts the new value of the range input field as input.
   */
  onChange?(value: number): void;
  /** Inline styles to be applied to the range input element (optional). */
  inputStyle?: React.CSSProperties;
}

export const RangeField = React.forwardRef<HTMLInputElement, RangeFieldProps>((props, ref) => {
  const {
    id,
    name,
    label,
    value,
    min = 0,
    helpText,
    disabled,
    onChange,
    step = 1,
    max = 100,
    inputStyle,
    labelClassName,
    defaultValue = 0,
  } = props;

  const [internalValue, setInternalValue] = useUncontrolled({
    value,
    defaultValue,
    finalValue: defaultValue,
    onChange,
  });
  const [fillPercentage, setFillPercentage] = React.useState(0);
  const uid = useUid(id);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(event.currentTarget.value as unknown as number);
  };

  React.useEffect(() => {
    const percent = (100 * (internalValue - min)) / (max - min);
    setFillPercentage(percent);
  }, [internalValue]);

  return (
    <label htmlFor={uid}>
      <p className={labelClassName}>{label}</p>
      <div className={classNames('merged-inputs')}>
        <input
          id={uid}
          ref={ref}
          min={min}
          max={max}
          name={name}
          step={step}
          type="range"
          disabled={disabled}
          onChange={handleChange}
          defaultValue={defaultValue}
          style={{
            ...inputStyle,
            backgroundImage: `linear-gradient(to right, rgb(0 0 0), rgb(0 0 0) ${fillPercentage}%, rgb(209 209 209) ${fillPercentage}%)`,
          }}
        />
        <span data-role="input-value">{internalValue}</span>
      </div>
      <small>{helpText}</small>
    </label>
  );
});

RangeField.displayName = 'RangeField';
